import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "@/components/Tooltip";
import type { InterludeTypes } from "@/interlude";

type SafeCustomFieldType = InterludeTypes["SafeCustomFieldType"];

interface TypeBadgeProps {
  fieldType: SafeCustomFieldType;
}

const styles = {
  badge: {
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: "20px",
    backgroundColor: theme.colors.staticBgPrimary,
    color: theme.colors.staticFgPrimary,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.33,
    textAlign: "center",
  },
  /**
   * An unknown type reads as inert rather than as one more type this build
   * supports. The label alone cannot carry that: `Unknown` sitting in the same
   * blue as `Text` looks like a fifth type, and the badge is the only signal
   * the row has -- the edit action is disabled with no explanation of its own.
   */
  unknownBadge: {
    backgroundColor: theme.colors.neutral020,
    color: theme.colors.staticFgNote,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const TypeBadge: React.FC<TypeBadgeProps> = function TypeBadge({
  fieldType,
}) {
  const { t } = useTranslation();

  const label = useMemo(() => {
    /**
     * A type this build has no name for, from a backend that added one ahead
     * of us. Throwing would take the whole table down over one row, so the
     * row renders and says only this much.
     *
     * The word is hardcoded and deliberately not translated, against this
     * repo's usual rule. It is an edge case no customer should meet, the raw
     * type value it stands in for was never translatable either, and a
     * catalog entry for it would outlive the situation. Decided, not missed.
     */
    if (!fieldType.expected) return "Unknown";

    const knownType = fieldType.value;
    switch (knownType) {
      case "text":
        return t("customField.fieldType.text");
      case "float":
        return t("customField.fieldType.number");
      case "date":
        return t("customField.fieldType.date");
      case "timestamp":
        return t("customField.fieldType.dateTime");
      case "single_select":
        return t("customField.fieldType.singleSelect");
      default:
        knownType satisfies never;
        throw new Error(inspectMessage`Invalid field type ${knownType}`);
    }
  }, [t, fieldType]);

  const badge: React.ReactElement = useMemo(
    () => (
      <Box
        component="span"
        /**
         * Focusable only when it carries the tooltip below, which is only the
         * unknown case. A known badge is plain text and has nothing to open.
         */
        tabIndex={fieldType.expected ? undefined : 0}
        sx={{
          ...styles.badge,
          ...(fieldType.expected ? {} : styles.unknownBadge),
        }}
      >
        {label}
      </Box>
    ),
    [fieldType.expected, label],
  );

  if (fieldType.expected) return badge;

  /**
   * The raw type value, for whoever has to work out which field the row is.
   * `Unknown` is the right label -- it is what the reader can act on, and the
   * raw value is a backend identifier with no bounded length -- but the value
   * still has to be reachable without opening devtools or the API response.
   *
   * Not translated, for the same reason the label is not: there is nothing in
   * a backend identifier to translate.
   *
   * The badge takes `tabIndex` so the tooltip opens on focus as well as on
   * hover. A `span` is not focusable on its own, and without it the raw value
   * would be reachable by mouse alone. The cost is one tab stop, and it only
   * appears on a row this build cannot render properly.
   *
   * Touch has neither hover nor focus, so the raw value stays out of reach
   * there. The badge and the disabled edit button still carry what a reader
   * needs in order to act on the row.
   */
  return (
    /**
     * `describeChild` is load-bearing. Without it MUI puts the title in the
     * child's `aria-label`, which replaces the badge's accessible name: a
     * screen reader would announce the backend identifier and never say
     * `Unknown`, leaving the one piece of information a listener can act on
     * unspoken. With it the raw value becomes a description instead, which is
     * what it is -- carried by the native `title` while the tooltip is closed
     * and by `aria-describedby` once it opens, so a screen reader reaches the
     * value without the tooltip having to open at all.
     */
    <Tooltip title={fieldType.raw} arrow describeChild>
      {badge}
    </Tooltip>
  );
};
